import {
  getRandomIv,
  convertArrayBufferToBase64String,
  convertBase64StringToArrayBuffer,
  concatArrayBuffers,
  getFromStorage,
  saveInStorage,
  debug,
} from "../util/Utility";
import Constants from "../constants/Constants";
import { decodeKey, derivePbkdf2Raw } from "./PBKDF2";
import { asRawKey } from "./KeyMaterial";
import SharedPreferences from "../constants/SharedPreferences";

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

/** UTF-8 encode for AEAD plaintext/AAD — matches Java StandardCharsets.UTF_8 */
const utf8Encode = (str) => textEncoder.encode(str ?? "");

/** UTF-8 decode for AEAD plaintext — matches Java UTF-8 string decode */
const utf8Decode = (buf) => textDecoder.decode(buf);

const isRawKeyMaterial = (key) =>
  key instanceof Uint8Array ||
  key instanceof ArrayBuffer ||
  (typeof ArrayBuffer !== "undefined" &&
    ArrayBuffer.isView &&
    ArrayBuffer.isView(key));

/**
 * Generates a fresh AES-256 key as raw bytes.
 * Canonical material for AES/AEAD — use encodeKey only for DB/network transport.
 * Matches Java Util.generateAesKeyRaw (no Base64 truncation).
 *
 * @returns {Promise<Uint8Array>} 32 cryptographically random bytes
 */
export const generateAesKey = async () => {
  const generatedKey = await window.crypto.subtle.generateKey(
    {
      name: "AES-GCM",
      length: 256,
    },
    true,
    ["encrypt", "decrypt"]
  );

  const exportedKey = await window.crypto.subtle.exportKey("raw", generatedKey);
  return new Uint8Array(exportedKey);
};

/**
 * Resolves AES-GCM CryptoKey from raw 32-byte material or legacy string key.
 *
 * @param {Uint8Array|ArrayBuffer|String} baseKeyMaterial
 * @returns {Promise<CryptoKey>}
 */
const resolveAesKey = async (baseKeyMaterial) => {
  if (isRawKeyMaterial(baseKeyMaterial)) {
    return importAesKeyRaw(baseKeyMaterial);
  }
  // Base64-encoded 32-byte keys from storage/HMAC wire form → decode, never UTF-8 the string
  if (typeof baseKeyMaterial === "string") {
    const raw = asRawKey(baseKeyMaterial);
    if (raw && raw.length === 32) {
      return importAesKeyRaw(raw);
    }
  }
  return loadAesKey(baseKeyMaterial);
};

/**
 * This function encrypts the plainText using AEAD and the given baseKey.
 *
 * @param {String} plaintext The text that is to be encrypted.
 * @param {String} associatedData The associated data to be used for AEAD.
 * @param {Uint8Array|ArrayBuffer|String} baseKeyMaterial Raw 32-byte key, or legacy string key.
 * @returns {String} concatenated information in the format <iv + "$" + tag + "$" + cipherText>.
 */
export const aeadEncryptWithAssociatedData = async (
  plaintext,
  associatedData,
  baseKeyMaterial
) => {
  const iv = getRandomIv();
  const baseKey = await resolveAesKey(baseKeyMaterial);

  // encrypt the plaintext
  const encryptedArrayBuffer = await window.crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv: iv,
      additionalData: utf8Encode(associatedData),
      tagLength: Constants.TAG_LENGTH,
    },
    baseKey,
    utf8Encode(plaintext)
  );

  // extract the tag and ciphertext from the result
  const tagArrayBuffer = encryptedArrayBuffer.slice(
    encryptedArrayBuffer.byteLength - ((Constants.TAG_LENGTH + 7) >> 3)
  );
  const cipherArrayBuffer = encryptedArrayBuffer.slice(
    0,
    encryptedArrayBuffer.byteLength - ((Constants.TAG_LENGTH + 7) >> 3)
  );

  // create a string in the required format
  const cipherTextInBase64 =
    convertArrayBufferToBase64String(cipherArrayBuffer);
  const tagInBase64 = convertArrayBufferToBase64String(tagArrayBuffer);
  const ivInBase64 = convertArrayBufferToBase64String(iv);

  //return `${ivInBase64}$${tagInBase64}$${cipherTextInBase64}`;
  var reValue = ivInBase64 + "$" + tagInBase64 + "$" + cipherTextInBase64;

  return reValue;
};

/**
 * This function decrypts the encryptedText using AEAD and the given baseKey.
 *
 * @param {String} encryptedTextAsString The encryptedText in format <iv + "$" + tag + "$" + cipherText>.
 * @param {String} associatedDataAsString The associated data to be used for AEAD.
 * @param {Uint8Array|ArrayBuffer|String} baseKeyMaterial Raw 32-byte key, or legacy string key.
 * @returns {String} Decrypted text.
 */
export const aeadDecryptWithAssociatedData = async (
  encryptedTextAsString,
  associatedDataAsString,
  baseKeyMaterial
) => {
  const baseKey = await resolveAesKey(baseKeyMaterial);
  // console.log("baseKey = ",baseKey);
  // split the input string into its components
  const encryptedParts = encryptedTextAsString.split("$");
  const ivArrayBuffer = convertBase64StringToArrayBuffer(encryptedParts[0]);
  const tagArrayBuffer = convertBase64StringToArrayBuffer(encryptedParts[1]);
  const cipherArrayBuffer = convertBase64StringToArrayBuffer(encryptedParts[2]);

  // decrypt the ciphertext
  try {
    // if(associatedDataAsString == 256){
    //   console.log("In 256");
    //   // var conStr=encryptedParts[1]+encryptedParts[2];
    //   // console.log("conStr = ",conStr);
    //   // console.log(convertBase64StringToArrayBuffer(conStr));
    //   const decryptedArrayBuffer = await window.crypto.subtle.decrypt(
    //     {
    //       name: "AES-GCM",
    //       iv: ivArrayBuffer,
    //       additionalData: convertStringToArrayBuffer(associatedDataAsString),
    //       tagLength: Constants.TAG_LENGTH,
    //     },
    //     baseKey,
    //     concatArrayBuffers(cipherArrayBuffer, tagArrayBuffer)
    //   );
    //   console.log("decryptedArrayBuffer = ",decryptedArrayBuffer);
    // // return the result as a string
    // return convertArrayBufferToString(decryptedArrayBuffer);
    // }else{
    const decryptedArrayBuffer = await window.crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: ivArrayBuffer,
        additionalData: utf8Encode(associatedDataAsString),
        tagLength: Constants.TAG_LENGTH,
      },
      baseKey,
      concatArrayBuffers(cipherArrayBuffer, tagArrayBuffer)
    );

    // return the result as a string
    return utf8Decode(decryptedArrayBuffer);
    // }
  } catch (e) {
    console.log("error = ", e);
    return;
  }
};

/**
 * This function encrypts the plaintext with AEAD using a key, created with passcode and keySalt,
 * as the baseKey.
 *
 * @param {String} plaintext The text that is to be encrypted.
 * @param {String} passcode The passcode used to create the baseKey.
 * @param {String} keySalt The keySalt used to create the baseKey.
 * @param {String} associatedData The associated data to be used for AEAD.
 * @returns {String} concatenated information in the format <iv + "$" + tag + "$" + cipherText>.
 */
export const aeadEncryptWithPasscode = async (
  plaintext,
  passcode,
  keySalt,
  associatedData
) => {
  // PBKDF2 raw 32 bytes used as AES-GCM key (never Base64 string-as-key)
  const baseKey = await derivePbkdf2Raw(passcode, keySalt);
  return await aeadEncryptWithAssociatedData(
    plaintext,
    associatedData,
    baseKey
  );
};

/**
 * This function decrypts the encryptedText with AEAD using a key, created with passcode and keySalt,
 * as the baseKey.
 *
 * @param {String} encryptedTextAsString The encryptedText in format <iv + "$" + tag + "$" + cipherText>.
 * @param {String} passcode The passcode used to create the baseKey.
 * @param {String} keySalt The keySalt used to create the baseKey.
 * @param {String} associatedDataAsString The associated data to be used for AEAD.
 * @returns {String} Decrypted text.
 */
export const aeadDecryptWithPasscode = async (
  encryptedTextAsString,
  passcode,
  keySalt,
  associatedDataAsString,
  sessionId
) => {
  // Raw 32-byte AES key: derive, or decode cached Base64 AUTH_PASSCODE_KEY
  var baseKey = null;
  if (
    passcode == null &&
    (await getFromStorage(sessionId, SharedPreferences.AUTH_PASSCODE_KEY))
  ) {
    const encodedKey = await getFromStorage(
      sessionId,
      SharedPreferences.AUTH_PASSCODE_KEY
    );
    await saveInStorage(sessionId, SharedPreferences.AUTH_PASSCODE_KEY, "");
    baseKey = decodeKey(encodedKey);
  } else {
    baseKey = await derivePbkdf2Raw(passcode, keySalt);
  }
  return await aeadDecryptWithAssociatedData(
    encryptedTextAsString,
    associatedDataAsString,
    baseKey
  );
};

/**
 * Imports raw 32-byte key material as an AES-GCM CryptoKey.
 *
 * @param {Uint8Array|ArrayBuffer} rawKey
 * @returns {Promise<CryptoKey>}
 */
export const importAesKeyRaw = async (rawKey) => {
  const keyData =
    rawKey instanceof ArrayBuffer
      ? rawKey
      : rawKey.buffer.slice(rawKey.byteOffset, rawKey.byteOffset + rawKey.byteLength);
  return await window.crypto.subtle.importKey("raw", keyData, "AES-GCM", true, [
    "encrypt",
    "decrypt",
  ]);
};

/**
 * Loads AES-GCM key from raw bytes or Base64-encoded 32-byte key string.
 *
 * @param {Uint8Array|ArrayBuffer|String} keyMaterial
 * @returns {Promise<CryptoKey>}
 */
export const loadAesKey = async (keyMaterial) => {
  if (isRawKeyMaterial(keyMaterial)) {
    return importAesKeyRaw(keyMaterial);
  }
  if (typeof keyMaterial === "string") {
    const raw = asRawKey(keyMaterial);
    if (raw) {
      return importAesKeyRaw(raw);
    }
  }
  throw new Error("loadAesKey: unsupported key material");
};

/**
 * Exports AES key as raw Uint8Array (canonical). Use encodeKey for transport.
 *
 * @param {CryptoKey} cryptoKey
 * @returns {Promise<Uint8Array>}
 */
export const exportAesKey = async (cryptoKey) => {
  const exported = await window.crypto.subtle.exportKey("raw", cryptoKey);
  return new Uint8Array(exported);
};

export const loadWrapAesKey = async (keyMaterial) => {
  let rawKey;
  if (isRawKeyMaterial(keyMaterial)) {
    rawKey =
      keyMaterial instanceof ArrayBuffer
        ? keyMaterial
        : keyMaterial.buffer.slice(
            keyMaterial.byteOffset,
            keyMaterial.byteOffset + keyMaterial.byteLength
          );
  } else if (typeof keyMaterial === "string") {
    const decoded = asRawKey(keyMaterial);
    if (!decoded) {
      throw new Error("loadWrapAesKey: could not decode key");
    }
    rawKey = decoded.buffer.slice(
      decoded.byteOffset,
      decoded.byteOffset + decoded.byteLength
    );
  } else {
    throw new Error("loadWrapAesKey: unsupported key material");
  }
  return await window.crypto.subtle.importKey(
    "raw",
    rawKey,
    { name: "AES-KW", length: 256 },
    true,
    ["wrapKey", "unwrapKey"]
  );
};

/**
 * This function specifically designed for encryption of text from base64 type of permanent storage key which encrypts plainText using AEAD.
 *
 * @param {String} plaintext The text that is to be encrypted.
 * @param {String} associatedData The associated data to be used for AEAD.
 * @param {String} baseKeyAsString The key that will form the base for the encryption of plainText.
 * @returns {String} concatenated information in the format <iv + "$" + tag + "$" + cipherText>.
 */
export const aeadEncryptWithStorageKey = async (
  plaintext,
  associatedData,
  baseKeyAsString
) => {
  const iv = getRandomIv();
  const baseKey = await loadAesKeyFromBase64(baseKeyAsString);
  // encrypt the plaintext
  const encryptedArrayBuffer = await window.crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv: iv,
      additionalData: utf8Encode(associatedData),
      tagLength: Constants.TAG_LENGTH,
    },
    baseKey,
    utf8Encode(plaintext)
  );

  // extract the tag and ciphertext from the result
  const tagArrayBuffer = encryptedArrayBuffer.slice(
    encryptedArrayBuffer.byteLength - ((Constants.TAG_LENGTH + 7) >> 3)
  );
  const cipherArrayBuffer = encryptedArrayBuffer.slice(
    0,
    encryptedArrayBuffer.byteLength - ((Constants.TAG_LENGTH + 7) >> 3)
  );

  // create a string in the required format
  const cipherTextInBase64 =
    convertArrayBufferToBase64String(cipherArrayBuffer);
  const tagInBase64 = convertArrayBufferToBase64String(tagArrayBuffer);
  const ivInBase64 = convertArrayBufferToBase64String(iv);

  //return `${ivInBase64}$${tagInBase64}$${cipherTextInBase64}`;
  var reValue = ivInBase64 + "$" + tagInBase64 + "$" + cipherTextInBase64;

  return reValue;
};

/**
 * This function specifically designed for decryption of text from base64 type of permanent storage key which decrypts plainText using AEAD.
 *
 * @param {String} plaintext The text that is to be decrypted.
 * @param {String} associatedDataAsString The associated data to be used for AEAD.
 * @param {String} baseKeyAsString The key that will form the base for the decryption.
 * @returns {String} Decrypted text.
 */
export const aeadDecryptWithStorageKey = async (
  encryptedTextAsString,
  associatedDataAsString,
  baseKeyAsString
) => {
  const baseKey = await loadAesKeyFromBase64(baseKeyAsString);
  const encryptedParts = encryptedTextAsString.split("$");
  const ivArrayBuffer = convertBase64StringToArrayBuffer(encryptedParts[0]);
  const tagArrayBuffer = convertBase64StringToArrayBuffer(encryptedParts[1]);
  const cipherArrayBuffer = convertBase64StringToArrayBuffer(encryptedParts[2]);
  // decrypt the ciphertext
  try {
    const decryptedArrayBuffer = await window.crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: ivArrayBuffer,
        additionalData: utf8Encode(associatedDataAsString),
        tagLength: Constants.TAG_LENGTH,
      },
      baseKey,
      concatArrayBuffers(cipherArrayBuffer, tagArrayBuffer)
    );
    // return the result as a string
    return utf8Decode(decryptedArrayBuffer);
    // }
  } catch (e) {
    console.log("error = ", e);
    throw e.message;
  }
};

export const loadAesKeyFromBase64 = async (keyAsString) => {
  const rawKey = convertBase64StringToArrayBuffer(keyAsString);
  return await window.crypto.subtle.importKey("raw", rawKey, "AES-GCM", true, [
    "encrypt",
    "decrypt",
  ]);
};
